import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomProperty } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { AuditHistoryDrawer } from '../components/AuditHistoryDrawer.js';
// CTSK-359 — lucide icons on Edit/Delete to match TagsAdmin /
// TaggingRulesAdmin. size=13 / strokeWidth=1.75 reads light and tight.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Pencil, Trash2, History } from 'lucide-react';

type PropertyType = 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'select' | 'document_link' | 'url' | 'user';

const TYPE_OPTIONS: { value: PropertyType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date (day precision — time resets to 00:00 UTC on operator edit)' },
  // CTSK-128 (2026-07-23): datetime CP type. Same storage shape as date
  // (ISO 8601 UTC in the CP jsonb) but the render layer preserves
  // sub-day precision on operator edit. Prefer this when the value
  // matters to the minute (crawl_started_at, session_last_activity_at,
  // scan_finished_at etc.); prefer 'date' when only the calendar day
  // matters (due dates, milestones).
  { value: 'datetime', label: 'Datetime (minute precision — preserves time on operator edit)' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'select', label: 'Select (list of options)' },
  { value: 'document_link', label: 'Document link' },
  { value: 'url', label: 'URL' },
  { value: 'user', label: 'User' },
];

export function CustomPropertiesAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['custom-properties'],
    queryFn: () => apiList<CustomProperty>('/custom-properties'),
  });
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; property: CustomProperty } | null>(null);
  // Fast-follow (CTSK-389) — no per-resource detail route for custom
  // properties either; same row-button-opens-drawer pattern as TagsAdmin.
  const [historyProperty, setHistoryProperty] = useState<CustomProperty | null>(null);

  // CTSK-359 (CTSK-85 analog) — client-side substring search on name.
  // Mirrors TagsAdmin's ctsk-069 pattern: pure client, no debounce —
  // the CP list is small (< few hundred) and filtering is a useMemo.
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const rows = data?.data ?? [];
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((p) => p.name.toLowerCase().includes(needle));
  }, [data?.data, q]);

  const { sorted, sort, setSort } = useTableSort(filtered, {
    name: (p: CustomProperty) => p.name,
    type: (p) => p.type,
    entity: (p) => p.entity,
    createdAt: (p) => p.createdAt,
  }, { key: 'name', dir: 'asc' });

  const del = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      apiFetch<void>(`/custom-properties/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Property deleted'); qc.invalidateQueries({ queryKey: ['custom-properties'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Custom properties</h1>
          <div className="page-subtitle">
            {data?.data.length ?? 0} properties · extend document metadata beyond built-in fields
            {q.trim() && data && (
              <span> · showing {filtered.length}</span>
            )}
          </div>
        </div>
        <button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New property</button>
      </div>

      {/* CTSK-359 — substring search on name, mirrors TagsAdmin. */}
      {data && data.data.length > 0 && (
        <div className="toolbar" style={{ flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search properties…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              flex: 1,
              maxWidth: 360,
            }}
          />
          {q.trim() && (
            <button className="btn btn--ghost" onClick={() => setQ('')}>
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading && <SkeletonRows rows={4} />}
      {data && data.data.length === 0 && (
        <EmptyState
          title="No custom properties"
          body="Define fields like Document Type, Project, Confidentiality — anything specific to your domain."
          action={<button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New property</button>}
        />
      )}
      {data && data.data.length > 0 && filtered.length === 0 && (
        <EmptyState
          title="No matches"
          body={`No property matches "${q}". Try a shorter search or clear it.`}
          action={<button className="btn" onClick={() => setQ('')}>Clear search</button>}
        />
      )}
      {data && filtered.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
              <th>Key</th>
              <SortableTh sortKey="type" sort={sort} onSortChange={setSort}>Type</SortableTh>
              <SortableTh sortKey="entity" sort={sort} onSortChange={setSort}>Entity</SortableTh>
              <th>Scope</th>
              <th>Required</th>
              <th>Usage</th>
              <SortableTh sortKey="createdAt" sort={sort} onSortChange={setSort}>Created</SortableTh>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id}>
                <td>
                  <strong>{p.name}</strong>
                  {p.description && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.description}</div>}
                  {p.installedByToolId && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 11, fontStyle: 'italic' }}>
                      seeded by a tool
                    </div>
                  )}
                </td>
                <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{p.key}</code></td>
                <td>{p.type}{p.type === 'select' && p.options.length > 0 && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> ({p.options.length} options)</span>
                )}</td>
                {/* CTSK-359 — entity column (document | work_item). Display-only;
                    the field always existed on the type, it just wasn't shown. */}
                <td>{p.entity}</td>
                <td>
                  {/* 0.1.11 — scope display. Empty = org-wide; otherwise show
                      the tag-name list as small chips so operators can see at
                      a glance which props appear on which docs. */}
                  {(p.appliesToTags?.length ?? 0) === 0 ? (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>org-wide</span>
                  ) : (
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                      {p.appliesToTags.map((name) => (
                        <span
                          key={name}
                          className="tag-chip"
                          title={`Only shows on documents tagged ${name}`}
                          style={{ fontSize: 11 }}
                        >
                          {name}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
                <td>{p.required ? '✓' : '—'}</td>
                <td>{p.usageCount}</td>
                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
                    <button
                      className="btn btn--ghost"
                      onClick={() => setDialog({ mode: 'edit', property: p })}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Pencil size={13} strokeWidth={1.75} aria-hidden /> Edit
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => setHistoryProperty(p)}
                      title={`View audit history for "${p.name}"`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <History size={13} strokeWidth={1.75} aria-hidden /> History
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => {
                        const force = p.usageCount > 0;
                        const msg = force
                          ? `Delete "${p.name}"? It is in use by ${p.usageCount} document(s) — values will be lost.`
                          : `Delete "${p.name}"?`;
                        if (confirm(msg)) del.mutate({ id: p.id, force });
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-primary)' }}
                    >
                      <Trash2 size={13} strokeWidth={1.75} aria-hidden /> Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dialog && (
        <PropertyDialog
          property={dialog.mode === 'edit' ? dialog.property : null}
          onCancel={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            qc.invalidateQueries({ queryKey: ['custom-properties'] });
          }}
        />
      )}

      {historyProperty && (
        <AuditHistoryDrawer
          resourceType="custom_property"
          resourceId={historyProperty.id}
          onClose={() => setHistoryProperty(null)}
        />
      )}
    </>
  );
}

function PropertyDialog({
  property,
  onCancel,
  onSaved,
}: {
  property: CustomProperty | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(property?.name ?? '');
  const [key, setKey] = useState(property?.key ?? '');
  const [type, setType] = useState<PropertyType>(property?.type ?? 'text');
  const [description, setDescription] = useState(property?.description ?? '');
  const [required, setRequired] = useState(property?.required ?? false);
  const [optionsRaw, setOptionsRaw] = useState((property?.options ?? []).join('\n'));
  // 0.1.11 — tag-name scope. Comma-separated input on save; chips on the
  // list view. Empty = org-wide (legacy default). Non-empty restricts
  // visibility to docs that carry any of the listed tag names.
  const [appliesToTagsRaw, setAppliesToTagsRaw] = useState((property?.appliesToTags ?? []).join(', '));

  const save = useMutation({
    mutationFn: () => {
      const options = type === 'select' ? optionsRaw.split('\n').map((s) => s.trim()).filter(Boolean) : [];
      const appliesToTags = appliesToTagsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        name,
        ...(property ? {} : { key: key || undefined }),
        type,
        description: description || undefined,
        required,
        options,
        appliesToTags,
      };
      return property
        ? apiSingle<CustomProperty>(`/custom-properties/${property.id}`, { method: 'PATCH', body })
        : apiSingle<CustomProperty>('/custom-properties', { method: 'POST', body });
    },
    onSuccess: () => { toast.success(property ? 'Property updated' : 'Property created'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      role="dialog"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }}
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ margin: 0, marginBottom: 16 }}>{property ? `Edit property: ${property.name}` : 'New custom property'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label htmlFor="cp-name">Name</label>
            <input id="cp-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div className="field">
            <label htmlFor="cp-key">Key (machine identifier)</label>
            <input
              id="cp-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="auto-generated from name"
              disabled={!!property}
              title={property ? 'Key cannot be changed after creation' : undefined}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="cp-type">Type</label>
          <select id="cp-type" value={type} onChange={(e) => setType(e.target.value as PropertyType)}>
            {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {property && property.usageCount > 0 && (
            <small style={{ color: 'var(--warning-primary)' }}>
              In use by {property.usageCount} document(s) — some type changes are restricted.
            </small>
          )}
        </div>

        <div className="field">
          <label htmlFor="cp-desc">Description</label>
          <textarea
            id="cp-desc"
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            style={{ padding: 8, border: '1px solid var(--border-strong)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        {type === 'select' && (
          <div className="field">
            <label htmlFor="cp-options">Options (one per line)</label>
            <textarea
              id="cp-options"
              value={optionsRaw}
              onChange={(e) => setOptionsRaw(e.target.value)}
              rows={5}
              placeholder="Internal&#10;External&#10;Confidential"
              style={{ padding: 8, border: '1px solid var(--border-strong)', borderRadius: 4, fontFamily: 'var(--font-mono)', resize: 'vertical' }}
            />
          </div>
        )}

        <div className="field">
          <label>
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />{' '}
            Required (documents must have a value)
          </label>
        </div>

        <div className="field">
          <label htmlFor="cp-applies">Applies to tags (comma-separated)</label>
          <input
            id="cp-applies"
            value={appliesToTagsRaw}
            onChange={(e) => setAppliesToTagsRaw(e.target.value)}
            placeholder="e.g. data-source, nest/website-grading"
          />
          <small style={{ color: 'var(--text-muted)' }}>
            Leave empty to show this property on every {(property?.entity ?? 'document')} in the
            org. When set, the property only shows on items that carry at least one of these
            tag names. The check is by tag name, not id.
          </small>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : (property ? 'Save changes' : 'Create property')}
          </button>
        </div>
      </div>
    </div>
  );
}
